<template>
    <div class="ak-overlay" @click.self="close">
        <div class="ak-modal wide">
            <div class="ak-modal-head">
                <div class="ak-modal-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.78 7.78 5.5 5.5 0 0 1 7.78-7.78zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                    </svg>
                </div>
                <div>
                    <h2>Create API Key</h2>
                    <p>The key is shown once after creation</p>
                </div>
            </div>
            <div class="ak-modal-body">
                <form class="ak-form" @submit.prevent="submit">
                    <div class="ak-row">
                        <div>
                            <label class="ak-label">Key name</label>
                            <input class="ak-input" v-model="form.name" required placeholder="e.g. Acme production">
                        </div>
                        <div>
                            <label class="ak-label">Client name</label>
                            <input class="ak-input" v-model="form.client_name" required placeholder="e.g. Acme Infra Pvt Ltd">
                        </div>
                    </div>
                    <div class="ak-row">
                        <div>
                            <label class="ak-label">Contact email</label>
                            <input class="ak-input" type="email" v-model="form.contact_email" required placeholder="dev@client.com">
                        </div>
                        <div>
                            <label class="ak-label">Plan</label>
                            <select class="ak-input" v-model.number="form.plan_id" required>
                                <option :value="null" disabled>Select a plan</option>
                                <option v-for="p in activePlans" :key="p.id" :value="p.id">{{ p.name }}</option>
                            </select>
                        </div>
                    </div>

                    <div class="ck-overrides">
                        <label class="ak-check">
                            <input type="checkbox" v-model="customize">
                            Customize limits for this key (overrides)
                        </label>
                        <p class="ak-hint">
                            By default the key follows its plan. Each override can be <strong>Inherit</strong> (use the plan),
                            <strong>Custom</strong> (a different value) or <strong>Unlimited</strong>. Quotas count tender records returned.
                        </p>
                        <div v-if="customize" class="ck-editor">
                            <ApiLimitsEditor v-model="overrides" :portals="portals" :allow-inherit="true" />
                        </div>
                    </div>

                    <div>
                        <label class="ak-label">IP allowlist (optional)</label>
                        <textarea class="ak-input" rows="2" v-model="ipText" placeholder="One IP or CIDR per line, e.g. 203.0.113.10 or 203.0.113.0/24"></textarea>
                        <p class="ak-hint">Leave empty to allow any IP.</p>
                    </div>

                    <div class="ak-row">
                        <div>
                            <label class="ak-label">Expiry</label>
                            <input class="ak-input" type="number" min="1" step="1" v-model.number="form.expires_in_days"
                                :disabled="neverExpires" placeholder="Days">
                            <label class="ak-check ck-never">
                                <input type="checkbox" v-model="neverExpires"> Never expires
                            </label>
                        </div>
                        <div>
                            <label class="ak-label">Signed requests</label>
                            <label class="ak-check ck-signed" :class="{ locked: signedLocked }">
                                <input type="checkbox" :checked="signedLocked || form.signed_mode" :disabled="signedLocked"
                                    :aria-describedby="signedLocked ? 'ck-signed-note' : null"
                                    @change="form.signed_mode = $event.target.checked">
                                Require HMAC-signed requests (a signing secret is issued)
                            </label>
                            <p v-if="signedLocked" id="ck-signed-note" class="ak-hint">Required by the selected plan, so it cannot be turned off here.</p>
                        </div>
                    </div>

                    <div>
                        <label class="ak-label">Your password (to confirm)</label>
                        <input class="ak-input" type="password" v-model="form.password" required autocomplete="current-password"
                            placeholder="Re-enter your admin password">
                    </div>

                    <div v-if="formError" class="ak-error">{{ formError }}</div>

                    <div class="ak-actions">
                        <button type="button" class="ak-btn ghost" @click="close">Cancel</button>
                        <button type="submit" class="ak-btn primary" :disabled="saving">
                            <span v-if="saving" class="ak-spinner"></span>
                            {{ saving ? 'Creating...' : 'Create Key' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import ApiLimitsEditor from './ApiLimitsEditor.vue';
import {
    authHeaders, errorMessage, makeOverrideState, overridePayload, validateState, parseIpList, invalidIps,
} from './apiKeysCommon';

export default {
    name: 'ApiKeyCreateModal',
    components: { ApiLimitsEditor },
    props: {
        plans: { type: Array, required: true },
        portals: { type: Array, default: () => [] },
    },
    emits: ['close', 'created'],
    data() {
        return {
            saving: false,
            formError: '',
            customize: false,
            neverExpires: false,
            ipText: '',
            overrides: makeOverrideState(null),
            form: {
                name: '', client_name: '', contact_email: '', plan_id: null,
                signed_mode: false, expires_in_days: 90, password: '',
            },
        };
    },
    computed: {
        activePlans() { return this.plans.filter(p => p.is_active); },
        // Mirrors the server: the plan's signed_mode_required flag, unless a per-key override sets it on/off.
        signedLocked() {
            const plan = this.plans.find(p => p.id === this.form.plan_id);
            const planFlag = !!plan?.features?.signed_mode_required;
            const o = this.customize ? this.overrides.features?.signed_mode_required : 'inherit';
            if (o === 'on') return true;
            if (o === 'off') return false;
            return planFlag;
        },
    },
    methods: {
        close() {
            if (!this.saving) this.$emit('close');
        },
        async submit() {
            if (this.saving) return;
            this.formError = '';
            const f = this.form;
            if (!f.name.trim() || !f.client_name.trim()) { this.formError = 'Key name and client name are required'; return; }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.contact_email)) { this.formError = 'Enter a valid contact email'; return; }
            if (!f.plan_id) { this.formError = 'Select a plan'; return; }
            if (!this.neverExpires && !(Number.isInteger(f.expires_in_days) && f.expires_in_days >= 1)) {
                this.formError = 'Expiry must be a whole number of days, or tick Never expires';
                return;
            }
            const ips = parseIpList(this.ipText);
            const bad = invalidIps(ips);
            if (bad.length) { this.formError = `Invalid IP/CIDR: ${bad.join(', ')}`; return; }
            if (this.customize) {
                const problem = validateState(this.overrides);
                if (problem) { this.formError = problem; return; }
            }
            if (!f.password) { this.formError = 'Enter your password to confirm'; return; }

            const body = {
                name: f.name.trim(),
                client_name: f.client_name.trim(),
                contact_email: f.contact_email.trim(),
                plan_id: f.plan_id,
                signed_mode: f.signed_mode || this.signedLocked,
                expires_in_days: this.neverExpires ? null : f.expires_in_days,
                password: f.password,
            };
            if (ips.length) body.ip_allowlist = ips;
            if (this.customize) {
                const o = overridePayload(this.overrides);
                if (Object.keys(o).length) body.overrides = o;
            }

            this.saving = true;
            try {
                const res = await axios.post(`${this.$API_URL}/super_admin/api-keys`, body, { headers: authHeaders() });
                this.form.password = '';
                this.$emit('created', res.data);
            } catch (err) {
                this.formError = errorMessage(err, 'Failed to create API key');
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.ck-overrides { display: flex; flex-direction: column; gap: 0.5rem; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 14px; padding: 1rem; }
.ck-overrides .ak-hint { margin: 0; }
.ck-editor { margin-top: 0.5rem; padding-top: 1rem; border-top: 1px solid #e2e8f0; }
.ck-never, .ck-signed { margin-top: 0.5rem; }
.ck-signed.locked { cursor: not-allowed; }
.ck-signed.locked input { cursor: not-allowed; }
</style>
